import React from "react";

/**
 * FullScreenViewer - Displays attachment in full screen modal
 * props:
 *  - attachment: { filename, content_type, data (base64), size }
 *  - onClose: function() - called to close the viewer
 */
export default function FullScreenViewer({ attachment, onClose }) {
  if (!attachment) return null;

  // Extract date from filename (format: -DDMMYY or -DDMMYY.)
  const extractDateFromFilename = (filename) => {
    if (!filename) return null;
    // Match pattern like -041125 or -041125. in filename
    const match = filename.match(/-(\d{6})(?:\.|$)/);
    if (!match) return null;
    
    const dateStr = match[1]; // e.g., "041125"
    const day = parseInt(dateStr.substring(0, 2), 10);
    const month = parseInt(dateStr.substring(2, 4), 10);
    const year = parseInt(dateStr.substring(4, 6), 10);
    
    // Convert 2-digit year to 4-digit (assuming 2000-2099)
    const fullYear = year < 50 ? 2000 + year : 1900 + year;
    
    // Validate date
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    
    try {
      const date = new Date(fullYear, month - 1, day);
      // Check if date is valid
      if (date.getFullYear() === fullYear && date.getMonth() === month - 1 && date.getDate() === day) {
        return date;
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  // Format graph date from filename as DD.MM.YYYY (e.g., "11.11.2025")
  const formatGraphDate = (date) => {
    if (!date || !(date instanceof Date)) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Get header text for attachment: graph date + graph image name
  const getAttachmentHeader = (attachment) => {
    const graphDate = extractDateFromFilename(attachment.filename);
    const formattedDate = graphDate ? formatGraphDate(graphDate) : null;
    const imageName = attachment.filename || "Unknown";
    
    if (formattedDate) {
      return `${formattedDate} - ${imageName}`;
    }
    return imageName;
  };

  // Helper to check if attachment is an image (by mimeType or file extension)
  const isImageFile = (att) => {
    const contentType = att.content_type || att.mimeType || "";
    const filename = (att.filename || "").toLowerCase();
    
    // Check mimeType first
    if (contentType.startsWith("image/")) {
      return true;
    }
    
    // Fallback: check file extension for common image formats
    const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"];
    return imageExtensions.some(ext => filename.endsWith(ext));
  };

  const isImage = isImageFile(attachment);
  const isPDF = attachment.content_type === "application/pdf" || attachment.mimeType === "application/pdf" || attachment.filename?.toLowerCase().endsWith(".pdf");
  // Use web_path URL if available, otherwise fall back to base64 data
  const fileUrl = attachment.web_path 
    ? `https://shipreportstvm.cloud/api/${attachment.web_path}` 
    : (attachment.data ? `data:${attachment.content_type || attachment.mimeType || "image/png"};base64,${attachment.data}` : null);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.95)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: "relative",
          maxWidth: "95%",
          maxHeight: "95%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "-40px",
            right: "0",
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            fontSize: "20px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2001,
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
          }}
        >
          ×
        </button>

        {/* Header - graph date + graph image name */}
        <div
          style={{
            color: "white",
            marginBottom: "15px",
            fontSize: "16px",
            fontWeight: "500",
            textAlign: "center",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={getAttachmentHeader(attachment)}
        >
          {getAttachmentHeader(attachment)}
        </div>

        {/* Content */}
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            maxWidth: "100%",
            maxHeight: "calc(95vh - 80px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isImage && fileUrl ? (
            <img
              src={fileUrl}
              alt={attachment.filename}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(95vh - 80px)",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : isPDF && fileUrl ? (
            <iframe
              src={fileUrl}
              style={{
                width: "90vw",
                height: "85vh",
                border: "none",
              }}
              title={attachment.filename}
            />
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#666",
              }}
            >
              <i className="fas fa-file" style={{ fontSize: "48px", marginBottom: "16px", display: "block" }} />
              <div>Preview not available for this file type</div>
              {fileUrl && (
                <a
                  href={fileUrl}
                  download={attachment.filename}
                  style={{
                    display: "inline-block",
                    marginTop: "16px",
                    padding: "8px 16px",
                    background: "#007bff",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                  }}
                >
                  Download File
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

